import React, { useState, useEffect, useRef, useMemo, forwardRef, useImperativeHandle, useCallback } from 'react';
import styled from 'styled-components';


const FilterSection = styled.div`
  margin-bottom: 1.5rem;
  position: relative;
  
  @media (max-width: 768px) {
    margin-bottom: 1rem;
  }
`;

const FilterTitle = styled.h3`
  font-size: 1rem;
  color: #2c3e50;
  margin-bottom: 0.75rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #e0e0e0;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
    padding: 0.5rem 0;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.5rem;
  margin-bottom: 0.75rem;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-size: 0.9rem;
  
  &:focus {
    outline: none;
    border-color: #28346c;
  }
  
  @media (max-width: 768px) {
    padding: 0.75rem;
    font-size: 1rem;
  }
`;

const FilterSelectButton = styled.div`
  width: 100%;
  padding: 0.5rem;
  margin-bottom: 0.75rem;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-size: 0.9rem;
  background-color: white;
  color: #757575;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  &:focus {
    outline: none;
    border-color: #28346c;
  }
  
  @media (max-width: 768px) {
    padding: 0.75rem;
    font-size: 1rem;
  }
`;

const FilterOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: 200px;
  overflow-y: auto;
  scrollbar-width: thin;
  padding-right: 0.5rem;
  background-color: white;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  position: absolute;
  width: 100%;
  z-index: 10;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }

  &::-webkit-scrollbar-thumb {
    background: #d1d1d1;
    border-radius: 4px;
  }
  
  @media (max-width: 768px) {
    position: fixed;
    width: 90%;
    max-width: 350px;
    max-height: 60vh;
    padding: 1.5rem;
    z-index: 1000;
    border-radius: 8px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25);
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
  }
`;

const FilterOption = styled.label`
  display: flex;
  align-items: center;
  font-size: 0.9rem;
  color: #2c3e50;
  cursor: pointer;
  padding: 8px;

  &:hover {
    background-color: #f5f5f5;
    color: #28346c;
  }
  
  @media (max-width: 768px) {
    padding: 12px 8px;
    font-size: 1rem;
  }
`;

const FilterCheckbox = styled.input`
  margin-right: 0.5rem;
  cursor: pointer;
  
  @media (max-width: 768px) {
    transform: scale(1.2);
    margin-right: 0.75rem;
  }
`;

const FilterCount = styled.span`
  margin-left: 0.25rem;
  color: #7f8c8d;
  font-size: 0.8rem;
`;

const NoResultsMessage = styled.div`
  font-size: 0.85rem;
  color: #7f8c8d;
  font-style: italic;
  padding: 0.5rem;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 1rem;
    text-align: center;
  }
`;

const Overlay = styled.div`
  display: ${props => props.$visible ? 'block' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  z-index: 999;
  backdrop-filter: blur(2px);
  pointer-events: ${props => props.$visible ? 'auto' : 'none'};
`;

const CloseButton = styled.button`
  display: none;
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  background: none;
  border: none;
  color: #28346c;
  font-size: 1.5rem;
  cursor: pointer;
  width: 36px;
  height: 36px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  
  &:hover {
    background-color: rgba(40, 52, 108, 0.1);
  }
  
  @media (max-width: 768px) {
    display: flex;
  }
`;




const JobMapFilters = forwardRef(({ jobs, onFilterChange, onFiltersUpdate, initialFilters }, ref) => {
  const [filters, setFilters] = useState({
    jobTitles: [],
    specialties: [],
    shifts: []
  });
  
  const [filterOptions, setFilterOptions] = useState({
    jobTitles: [],
    specialties: [],
    shifts: []
  });
  
  const [searchTerms, setSearchTerms] = useState({
    jobTitles: '',
    specialties: '',
    shifts: ''
  });
  
  const [activeFilter, setActiveFilter] = useState(null);
  const [overlayVisible, setOverlayVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const jobTitlesRef = useRef(null);
  const specialtiesRef = useRef(null);
  const shiftsRef = useRef(null);
  
  const filterRefs = useMemo(() => ({
    jobTitles: jobTitlesRef,
    specialties: specialtiesRef,
    shifts: shiftsRef
  }), []);

  const extractFilterOptions = (jobsData) => {
    const options = {
      jobTitles: new Set(),
      specialties: new Set(),
      shifts: new Set()
    };
    
    jobsData.forEach(job => {
      if (job.JobTitlen) {
        options.jobTitles.add(job.JobTitlen);
      } else if (job.VMS_Job_Title__c) {
        options.jobTitles.add(job.VMS_Job_Title__c);
      } else if (job.AVTRRT__Job_Title__c) {
        options.jobTitles.add(job.AVTRRT__Job_Title__c);
      } else if (job.Job_Title__c) {
        options.jobTitles.add(job.Job_Title__c);
      }
      
      if (job.Specialityn) {
        options.specialties.add(job.Specialityn);
      } else if (job.Specialty) {
        options.specialties.add(job.Specialty);
      }
      
      if (job.Shiftn) {
        options.shifts.add(job.Shiftn);
      } else if (job.JR_Shift) {
        options.shifts.add(job.JR_Shift);
      }
    });
    
    setFilterOptions({
      jobTitles: Array.from(options.jobTitles).sort(),
      specialties: Array.from(options.specialties).sort(),
      shifts: Array.from(options.shifts).sort()
    });
  };

  const applyFilters = useCallback(() => {
    const filteredJobs = jobs.filter(job => {
      if (filters.jobTitles.length > 0) {
        let jobTitleMatch = false;
        
        if (job.JobTitlen && filters.jobTitles.includes(job.JobTitlen)) {
          jobTitleMatch = true;
        } else if (job.VMS_Job_Title__c && filters.jobTitles.includes(job.VMS_Job_Title__c)) {
          jobTitleMatch = true;
        } else if (job.AVTRRT__Job_Title__c && filters.jobTitles.includes(job.AVTRRT__Job_Title__c)) {
          jobTitleMatch = true;
        } else if (job.Job_Title__c && filters.jobTitles.includes(job.Job_Title__c)) {
          jobTitleMatch = true;
        }
        
        if (!jobTitleMatch) {
          return false;
        }
      }
      
      if (filters.specialties.length > 0) {
        if (job.Specialityn && filters.specialties.includes(job.Specialityn)) {
        } else if (job.Specialty && filters.specialties.includes(job.Specialty)) {
        } else {
          return false;
        }
      }
      
      if (filters.shifts.length > 0) {
        if (job.Shiftn && filters.shifts.includes(job.Shiftn)) {
        } else if (job.JR_Shift && filters.shifts.includes(job.JR_Shift)) {
        } else {
          return false;
        }
      }
      
      return true;
    });

    onFilterChange(filteredJobs);
  }, [jobs, filters, onFilterChange]);

  const handleFilterChange = (category, value) => {
    setFilters(prev => {
      const updatedFilters = { ...prev };
      if (updatedFilters[category].includes(value)) {
        updatedFilters[category] = updatedFilters[category].filter(item => item !== value);
      } else {
        updatedFilters[category] = [...updatedFilters[category], value];
      }
      
      // Notify parent of filter changes
      if (onFiltersUpdate) {
        onFiltersUpdate(updatedFilters);
      }
      
      return updatedFilters;
    });
  };

  const handleSearchChange = (category, value) => {
    setSearchTerms(prev => ({
      ...prev,
      [category]: value
    }));
  };

  const handleFilterClick = (category, event) => {
    setActiveFilter(category);
    
    if (window.innerWidth <= 768) {
      setOverlayVisible(true);
    }
  };
  
  const handleSearchFocus = (category) => {
    setActiveFilter(category);
    if (window.innerWidth <= 768) {
      setOverlayVisible(true);
    }
  };
  
  const handleSearchBlur = () => {
    if (!isMobile) return;
    setTimeout(() => {
      setActiveFilter(null);
      setOverlayVisible(false);
    }, 150);
  };
  
  const handleCloseFilter = () => {
    setActiveFilter(null);
    setOverlayVisible(false);
  };

  const getFilteredOptions = (category) => {
    return filterOptions[category].filter(option => 
      option.toLowerCase().includes(searchTerms[category].toLowerCase())
    ).sort((a, b) => {
      const countA = countJobsForFilter(category, a);
      const countB = countJobsForFilter(category, b);
      
      if (countA !== countB) {
        return countB - countA;
      }
      
      return a.localeCompare(b);
    });
  };

  const clearAllFilters = useCallback(() => {
    const clearedFilters = {
      jobTitles: [],
      specialties: [],
      shifts: []
    };
    
    setFilters(clearedFilters);
    
    setSearchTerms({
      jobTitles: '',
      specialties: '',
      shifts: ''
    });
    
    // Notify parent of filter changes
    if (onFiltersUpdate) {
      onFiltersUpdate(clearedFilters);
    }
  }, [onFiltersUpdate]);

  const removeFilter = useCallback((category, value) => {
    setFilters(prev => {
      const updatedFilters = {
        ...prev,
        [category]: prev[category].filter(item => item !== value)
      };
      
      // Notify parent of filter changes
      if (onFiltersUpdate) {
        onFiltersUpdate(updatedFilters);
      }
      
      return updatedFilters;
    });
  }, [onFiltersUpdate]);

  const countJobsForFilter = (category, value) => {
    return jobs.filter(job => {
      switch(category) {
        case 'jobTitles':
          return (job.JobTitlen === value || job.VMS_Job_Title__c === value || 
                  job.AVTRRT__Job_Title__c === value || job.Job_Title__c === value);
        case 'specialties':
          return (job.Specialityn === value || job.Specialty === value);
        case 'shifts':
          return (job.Shiftn === value || job.JR_Shift === value);
        default:
          return false;
      }
    }).length;
  };


  const getPlaceholderText = (category) => {
    const selectedCount = filters[category].length;
    
    if (selectedCount === 0) {
      switch(category) {
        case 'jobTitles': return 'Select job titles...';
        case 'specialties': return 'Select specialties...';
        case 'shifts': return 'Select shifts...';
        default: return 'Select options...';
      }
    } else {
      return `${selectedCount} selected`;
    }
  };

  const renderFilterInput = (category) => {
    if (isMobile) {
      return (
        <FilterSelectButton
          onClick={(e) => handleFilterClick(category, e)}
        >
          {getPlaceholderText(category)}
          <span>▼</span>
        </FilterSelectButton>
      );
    } else {
      return (
        <div style={{ position: 'relative' }}>
          <SearchInput 
            type="text"
            placeholder={getPlaceholderText(category)}
            value={searchTerms[category]}
            onChange={(e) => handleSearchChange(category, e.target.value)}
            onFocus={() => handleSearchFocus(category)}
            onBlur={() => { if (isMobile) handleSearchBlur(); }}
          />
          <span style={{
            position: 'absolute',
            right: '0.75rem',
            top: '40%',
            transform: 'translateY(-50%)',
            pointerEvents: 'none',
            fontSize: '0.8rem',
            color: '#666'
          }}>▼</span>
        </div>
      );
    }
  };

  useImperativeHandle(ref, () => ({
    resetFilters: clearAllFilters,
    removeFilter: removeFilter
  }), [clearAllFilters, removeFilter]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (!isMobile && activeFilter !== null) {
      const handleClickOutside = (event) => {
        if (
          filterRefs[activeFilter] && 
          !filterRefs[activeFilter].current?.contains(event.target)
        ) {
          setActiveFilter(null);
        }
      };
      
      document.addEventListener('mousedown', handleClickOutside);
      
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [activeFilter, isMobile, filterRefs]);

  useEffect(() => {
    if (jobs.length > 0) {
      extractFilterOptions(jobs);
    }
  }, [jobs]);

  useEffect(() => {
    if (initialFilters && filterOptions.jobTitles.length > 0) {
      // Match URL params with available filter options
      const matchedFilters = {
        jobTitles: [],
        specialties: [],
        shifts: []
      };

      // Match job titles
      if (initialFilters.jobTitles && initialFilters.jobTitles.length > 0) {
        initialFilters.jobTitles.forEach(title => {
          const titleLower = title.toLowerCase();
          const exact = filterOptions.jobTitles.find(t => t.toLowerCase() === titleLower);
          if (exact) {
            matchedFilters.jobTitles.push(exact);
          } else {
            const partial = filterOptions.jobTitles.find(t => t.toLowerCase().includes(titleLower));
            if (partial) {
              matchedFilters.jobTitles.push(partial);
            }
          }
        });
      }

      // Match specialties
      if (initialFilters.specialties && initialFilters.specialties.length > 0) {
        initialFilters.specialties.forEach(specialty => {
          const specialtyLower = specialty.toLowerCase();
          const exact = filterOptions.specialties.find(s => s.toLowerCase() === specialtyLower);
          if (exact) {
            matchedFilters.specialties.push(exact);
          } else {
            const partial = filterOptions.specialties.find(s => s.toLowerCase().includes(specialtyLower));
            if (partial) {
              matchedFilters.specialties.push(partial);
            }
          }
        });
      }

      // Match shifts
      if (initialFilters.shifts && initialFilters.shifts.length > 0) {
        initialFilters.shifts.forEach(shift => {
          const shiftLower = shift.toLowerCase();
          const exact = filterOptions.shifts.find(s => s.toLowerCase() === shiftLower);
          if (exact) {
            matchedFilters.shifts.push(exact);
          }
        });
      }

      // Only set filters if any matches were found
      if (matchedFilters.jobTitles.length > 0 || matchedFilters.specialties.length > 0 || matchedFilters.shifts.length > 0) {
        setFilters(matchedFilters);
        if (onFiltersUpdate) {
          onFiltersUpdate(matchedFilters);
        }
      }
    }
  }, [initialFilters, filterOptions, onFiltersUpdate]);

  useEffect(() => {
    applyFilters();
  }, [filters, jobs, applyFilters]);

  return (
    <div>
      <Overlay $visible={overlayVisible} onClick={handleCloseFilter} />
      
      

      {filterOptions.jobTitles.length > 0 && (
        <FilterSection ref={filterRefs.jobTitles}>
          <FilterTitle>Job Title</FilterTitle>
          {renderFilterInput('jobTitles')}
          {activeFilter === 'jobTitles' && (
            <FilterOptions>
              <CloseButton onClick={handleCloseFilter}>×</CloseButton>
              {getFilteredOptions('jobTitles').length > 0 ? (
                getFilteredOptions('jobTitles').map((jobTitle) => (
                  <FilterOption key={`jobTitle-${jobTitle}`}>
                    <FilterCheckbox 
                      type="checkbox"
                      checked={filters.jobTitles.includes(jobTitle)}
                      onChange={() => handleFilterChange('jobTitles', jobTitle)}
                    />
                    {jobTitle}
                    <FilterCount>({countJobsForFilter('jobTitles', jobTitle)})</FilterCount>
                  </FilterOption>
                ))
              ) : (
                <NoResultsMessage>No job titles match your search</NoResultsMessage>
              )}
            </FilterOptions>
          )}
        </FilterSection>
      )}

      {filterOptions.specialties.length > 0 && (
        <FilterSection ref={filterRefs.specialties}>
          <FilterTitle>Specialty</FilterTitle>
          {renderFilterInput('specialties')}
          {activeFilter === 'specialties' && (
            <FilterOptions>
              <CloseButton onClick={handleCloseFilter}>×</CloseButton>
              {getFilteredOptions('specialties').length > 0 ? (
                getFilteredOptions('specialties').map((specialty) => (
                  <FilterOption key={`specialty-${specialty}`}>
                    <FilterCheckbox 
                      type="checkbox"
                      checked={filters.specialties.includes(specialty)}
                      onChange={() => handleFilterChange('specialties', specialty)}
                    />
                    {specialty}
                    <FilterCount>({countJobsForFilter('specialties', specialty)})</FilterCount>
                  </FilterOption>
                ))
              ) : (
                <NoResultsMessage>No specialties match your search</NoResultsMessage>
              )}
            </FilterOptions>
          )}
        </FilterSection>
      )}

      {filterOptions.shifts.length > 0 && (
        <FilterSection ref={filterRefs.shifts}>
          <FilterTitle>Shift</FilterTitle>
          {renderFilterInput('shifts')}
          {activeFilter === 'shifts' && (
            <FilterOptions>
              <CloseButton onClick={handleCloseFilter}>×</CloseButton>
              {getFilteredOptions('shifts').length > 0 ? (
                getFilteredOptions('shifts').map((shift) => (
                  <FilterOption key={`shift-${shift}`}>
                    <FilterCheckbox 
                      type="checkbox"
                      checked={filters.shifts.includes(shift)}
                      onChange={() => handleFilterChange('shifts', shift)}
                    />
                    {shift}
                    <FilterCount>({countJobsForFilter('shifts', shift)})</FilterCount>
                  </FilterOption>
                ))
              ) : (
                <NoResultsMessage>No shifts match your search</NoResultsMessage>
              )}
            </FilterOptions>
          )}
        </FilterSection>
      )}


    </div>
  );
});

export default JobMapFilters;